// src/pages/LoginCallback.tsx
import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setTokens } from "src/utils/tokenCookie";
import { useAuthStore } from "src/store/Auth/auth.store";
import type { MenuItem } from "src/types/menu/menuTypes";

type TokenData = {
  tokenType: string;
  accessToken: string;
  refreshToken: string;
  userId: number;
  name: string;
  email: string;
  menuTree?: MenuItem[];
};

type ApiResponse<T> = {
  statusCode: number;
  message: string;
  data: T;
};

export default function LoginCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const setLogin = useAuthStore((s) => s.setLogin);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get("code");
    if (!code) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_OMS_SERVER_API}/v1/admin/auth/google/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
          }
        );

        if (!res.ok) throw new Error(`토큰 교환 실패 (${res.status})`);

        const json: ApiResponse<TokenData> = await res.json();

        if (json.statusCode !== 1) {
          throw new Error(json.message || "토큰 교환 실패");
        }

        const { accessToken, refreshToken, userId, email, name, menuTree } = json.data;
        if (!accessToken || !refreshToken) throw new Error("토큰이 비어있음");

        setTokens(accessToken, refreshToken);

        // 사용자 정보 localStorage에 저장
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("userId", String(userId));
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userName", name);
        localStorage.setItem("loginType", "admin");

        // menuTree를 store에 저장
        setLogin(accessToken, menuTree ?? []);

        navigate("/", { replace: true });
      } catch (e) {
        console.error("LoginCallback error:", e);
        navigate("/login", { replace: true });
      }
    })();
  }, [params, navigate, setLogin]);

  return <div style={{ padding: 24 }}>로그인 처리 중...</div>;
}
